import type {CombinatorKey} from '@packages/systems/core/utils/StyleTypes';
import {type Qualified, getPluginName} from '@packages/systems/wfdl-Qualified';
import type {StyleBlockId} from '@packages/systems/style/types';
import type {LibrariesInstallationType, LibraryManifest} from './types';
import {LIBRARY_EXPORT_TYPES, LIBRARY_SOURCE} from './constants';
import {getLibraryCapabilities} from './utils/getLibraryCapabilities';

/**
 * The rules that decide whether a library shares tag styles with the site, so
 * the panel, the canvas and the compiler all govern the same set.
 */
export function isLibraryWithTagStyles<IDType>({
  source,
  profileId,
  traits,
  exports,
}: Pick<
  LibraryManifest<IDType>,
  'source' | 'profileId' | 'traits' | 'exports'
>): boolean {
  // Skip libraries without a profile (sample/seeded entries).
  if (!profileId.length) {
    return false;
  }

  // Only Workspace libraries (exclude Marketplace).
  if (source !== LIBRARY_SOURCE.WORKSPACE) {
    return false;
  }

  // Skip hidden libraries (e.g. AI Site Gen)
  if (getLibraryCapabilities({traits}).isHidden) {
    return false;
  }

  // Only libraries that actually expose at least one tag style.
  return exports.some((exp) => exp.type === LIBRARY_EXPORT_TYPES.TAG_STYLE);
}

/**
 * One installed library's tag styles settings on a site. The array these live
 * in is the source of truth for both ordering and active state: order is
 * priority — last wins, as in the cascade — and `isActive` is whether the tag
 * styles are applied. Keeping the two separate lets a disabled library hold a
 * position.
 *
 * `profileId` is the SharedResourceProfile id, which survives a library update
 * — unlike the VersionedResource id, which changes on every one. A style block
 * carries the version id in `origin[0]`, so joining settings to blocks needs a
 * profile-to-version translation on the read side.
 *
 * A library with no entry has never been configured: it sorts first, which is
 * the lowest priority, and its tag styles still apply. Install writes the only
 * entry the server ever adds, so a library installed before this feature
 * shipped has no entry at all — the
 * absence has to read as "not configured", never as "switched off". The panel
 * writes one per installed library from the first change onwards.
 */
export type LibraryTagStylesSettings = {
  profileId: string;
  isActive: boolean;
};

/**
 * `SiteMeta.libraryTagStylesSettings` as it comes off the document: Mongoose
 * subdocuments whose fields are untyped, and absent on a site that has none.
 */
export type LibraryTagStylesSettingsSubdocument =
  | readonly {profileId: unknown; isActive: unknown}[]
  | null
  | undefined;

/**
 * Rebuilds the entries as plain objects. The ones persisted on SiteMeta are
 * Mongoose subdocuments, and spreading one carries its internals along, so
 * every read and write of the field goes through here.
 */
export function toLibraryTagStylesSettings(
  settings: LibraryTagStylesSettingsSubdocument
): LibraryTagStylesSettings[] {
  return (settings ?? []).map(({profileId, isActive}) => ({
    profileId: String(profileId),
    isActive: Boolean(isActive),
  }));
}

/**
 * The stored settings keyed by the installed library version whose style blocks
 * they govern. The key is the VersionedResource id a block carries in
 * `origin[0]`, so a block resolves its own library in one lookup rather than a
 * scan — `isHiddenLibraryTagStyle` runs once per style block, so a scan here
 * would cost blocks × libraries.
 *
 * `order` is the library's place in the site's tag styles priority order,
 * lowest first — the rank `sortStyleBlocksForCascade` applies and the row order
 * the panel renders. Carried on the entry rather than left implicit in the key
 * order, so nothing depends on object key iteration surviving a round trip.
 */
export type InstalledLibraryTagStylesSettings = {
  readonly [pluginId: string]: LibraryTagStylesSettings & {order: number};
};

/**
 * The installed libraries keyed by the plugin id their style blocks carry, each
 * paired with its stored active state and stamped with its place in the
 * priority order.
 *
 * The single place that order is computed. Both the canvas and the tag styles
 * panel read this result, so the rank the cascade applies and the row order the
 * user drags cannot disagree, and neither recomputes it.
 *
 * Iterating the installations rather than the settings is what makes the result
 * total: every installed library gets an entry, so a library with no stored
 * setting still holds a rank, and a setting whose library is gone produces no
 * entry and can neither rank nor hide anything.
 *
 * A library with no stored setting applies its tag styles. Only the
 * install path writes an entry, so the absence means the site was never asked,
 * not that the answer was no — and before this feature every library's tag
 * styles applied. Switching one off always writes an entry, so the deliberate
 * "no" is never confused with silence.
 *
 * An empty result means no library's tag styles are governed here, so nothing
 * is hidden and nothing is ranked.
 */
export function toInstalledLibraryTagStylesSettings(
  settings: readonly LibraryTagStylesSettings[],
  installedLibraries: readonly Pick<
    LibraryManifest<string>,
    'profileId' | 'pluginId'
  >[]
): InstalledLibraryTagStylesSettings {
  const positionByProfileId = new Map<string, number>();
  const isActiveByProfileId = new Map<string, boolean>();
  settings.forEach(({profileId, isActive}, position) => {
    positionByProfileId.set(profileId, position);
    isActiveByProfileId.set(profileId, isActive);
  });

  // -1 undercuts every stored position, so a library with no setting ranks
  // lowest, and equal positions subtract to 0, which a stable sort leaves in
  // installed order. `Infinity` cannot stand in here: the difference of two is
  // `NaN`, and a comparator returning `NaN` is undefined behavior.
  const positionOf = (profileId: string) =>
    positionByProfileId.get(profileId) ?? -1;

  const byPluginId: Record<string, LibraryTagStylesSettings & {order: number}> =
    {};
  [...installedLibraries]
    .sort((a, b) => positionOf(a.profileId) - positionOf(b.profileId))
    .forEach(({profileId, pluginId}, order) => {
      byPluginId[pluginId] = {
        profileId,
        isActive: isActiveByProfileId.get(profileId) ?? true,
        order,
      };
    });
  return byPluginId;
}

/**
 * The same settings, additionally keyed by the plugin id of every version that
 * is mid-install, carrying the settings of the version it replaces.
 *
 * An update re-stamps the site's style blocks with the incoming version's
 * plugin id before `DesignerStore.libraries` swaps to the new manifest. The
 * join cannot know that id yet, and an absent entry applies — that is the
 * window an inactive library's tag styles flash on.
 *
 * Only fills in keys the join left absent, so it is a no-op in steady state and
 * stops applying once the swap produces the entry for real.
 */
export function hydrateInstallingLibrariesTagStylesSettings(
  settings: InstalledLibraryTagStylesSettings,
  installations: LibrariesInstallationType
): InstalledLibraryTagStylesSettings {
  const installedEntries = Object.entries(installations);
  if (installedEntries.length === 0) {
    return settings;
  }

  const entryByProfileId = new Map<
    string,
    InstalledLibraryTagStylesSettings[string]
  >();
  for (const entry of Object.values(settings)) {
    entryByProfileId.set(entry.profileId, entry);
  }

  // Mutable because the public type's index signature is read-only, the same
  // reason the join above builds into a local before returning it.
  let hydrated: Record<
    string,
    InstalledLibraryTagStylesSettings[string]
  > | null = null;
  for (const [pluginId, {profileId}] of installedEntries) {
    if (settings[pluginId] != null) {
      continue;
    }

    const entry = entryByProfileId.get(profileId);
    if (entry == null) {
      continue;
    }

    // The very same entry under both ids, so the incoming version ranks exactly
    // where the outgoing one sat.
    hydrated ??= {...settings};
    hydrated[pluginId] = entry;
  }

  // Nothing added means the original reference: a fresh value misses every
  // `weakMemo` on `styleBlockState` and forces a full canvas CSSOM resync.
  return hydrated ?? settings;
}

/**
 * A stable empty reference for "no library's tag styles are governed here", so
 * nothing is hidden and nothing is ranked. Shared so the consumers that diff
 * this value by identity — the CSSOM sync, the separate-origin canvas — all see
 * one reference rather than a fresh object each.
 */
export const EMPTY_INSTALLED_LIBRARY_TAG_STYLES_SETTINGS: InstalledLibraryTagStylesSettings =
  {};

/**
 * The fields the predicates below read off a style block. Named because blocks
 * reach them projected rather than whole: the server keeps these under
 * `block.data`, the Designer flat on an Immutable record.
 */
export type TagStyleFields = {
  type: string;
  fake?: boolean;
  comb?: CombinatorKey | null;
  origin?: Qualified<StyleBlockId> | null;
};

/**
 * Whether a style block is a tag style — the shape a library ships as a tag
 * styles export. Deliberately says nothing about `origin`, so it holds for a
 * site's own block and a library's alike. The `comb` check is what excludes a
 * nested tag selector, e.g. the `p` inside a library component, which carries a
 * combinator and is not a tag style.
 *
 * Accepts the fields it inspects rather than a block, so it reads the server's
 * `block.data.*` and the Designer's flat Immutable record the same way.
 */
export function isTagStyle({type, fake, comb}: TagStyleFields): boolean {
  return type === 'tag' && !fake && !comb;
}

/**
 * Whether a tag style belongs to a library rather than to the site — the exact
 * complement of `isExportableTagStyle`. Narrows `origin`, so a caller can reach
 * for `getPluginName` without a second null check.
 */
export function isLibraryTagStyle(
  fields: TagStyleFields
): fields is TagStyleFields & {origin: Qualified<StyleBlockId>} {
  return isTagStyle(fields) && fields.origin != null;
}

/**
 * Whether a block's rule must be left out of the generated CSS: a tag style
 * carrying an `origin` belongs to a library rather than to the site, and that
 * library does not apply its tag styles. Anything else — a site tag style, a
 * library's class or element block, a nested tag selector — is never hidden.
 *
 * Only a library that holds a row and has switched its tag styles off is
 * hidden. A block whose library has no row is left alone: the settings do not
 * govern that library — it ships no tag style export, or predates the feature —
 * and hiding it would strip styles the user has no row to turn back on.
 */
export function isHiddenLibraryTagStyle(
  block: TagStyleFields,
  installed: InstalledLibraryTagStylesSettings
): boolean {
  if (!isLibraryTagStyle(block)) {
    return false;
  }

  const setting = installed[getPluginName(block.origin)];
  return setting != null && !setting.isActive;
}
